import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { getPublications, createPublication, updatePublication, deletePublication } from '../api';

function Publications({ user, onLogout }) {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    authors: '',
    journal: '',
    year: '',
    volume: '',
    pages: '',
    doi: '',
    abstract: '',
    keywords: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadPublications();
  }, []);

  const loadPublications = async () => {
    try {
      const response = await getPublications();
      setPublications(response.data);
    } catch (err) {
      setError('Error loading publications');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      authors: '',
      journal: '',
      year: '',
      volume: '',
      pages: '',
      doi: '',
      abstract: '',
      keywords: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (pub) => {
    setFormData({
      title: pub.title || '',
      authors: pub.authors || '',
      journal: pub.journal || '',
      year: pub.year || '',
      volume: pub.volume || '',
      pages: pub.pages || '',
      doi: pub.doi || '',
      abstract: pub.abstract || '',
      keywords: pub.keywords || ''
    });
    setEditingId(pub._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this publication?')) {
      try {
        setError('');
        console.log('Deleting publication with ID:', id);
        const response = await deletePublication(id);
        console.log('Delete response:', response);
        await loadPublications();
        alert('Publication deleted successfully');
      } catch (err) {
        const errorMsg = err.response?.data?.error || 'Error deleting publication';
        setError(errorMsg);
        console.error('Delete publication error:', err);
        console.error('Error response:', err.response?.data);
        alert(`Failed to delete publication: ${errorMsg}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await updatePublication(editingId, formData);
      } else {
        await createPublication(formData);
      }
      setShowModal(false);
      resetForm();
      loadPublications();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving publication');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="main-content">
        <div className="page-header">
          <h1>Publications</h1>
          <p>Manage your scientific publications</p>
        </div>

        <div className="content-section">
          <div className="section-header">
            <h2>My Publications</h2>
            <button className="btn btn-success" onClick={handleAdd}>
              + Add Publication
            </button>
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : publications.length === 0 ? (
            <div className="empty-state">
              <p>No publications yet</p>
              <button className="btn btn-primary" onClick={handleAdd}>
                Add your first publication
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Authors</th>
                    <th>Journal</th>
                    <th>Year</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {publications.map((pub) => (
                    <tr key={pub._id}>
                      <td data-label="Title">{pub.title}</td>
                      <td data-label="Authors">{pub.authors}</td>
                      <td data-label="Journal">{pub.journal || '-'}</td>
                      <td data-label="Year">{pub.year || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-small"
                            onClick={() => handleEdit(pub)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-small"
                            onClick={() => handleDelete(pub._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? 'Edit Publication' : 'Add Publication'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="error-message">{error}</div>}
                  
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Authors *</label>
                    <input
                      type="text"
                      name="authors"
                      value={formData.authors}
                      onChange={handleChange}
                      placeholder="e.g., Smith J., Doe A."
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Journal</label>
                    <input
                      type="text"
                      name="journal"
                      value={formData.journal}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      min="1900"
                      max="2100"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Volume</label>
                    <input
                      type="text"
                      name="volume"
                      value={formData.volume}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Pages</label>
                    <input
                      type="text"
                      name="pages"
                      value={formData.pages}
                      onChange={handleChange}
                      placeholder="e.g., 123-145"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>DOI</label>
                    <input
                      type="text"
                      name="doi"
                      value={formData.doi}
                      onChange={handleChange}
                      placeholder="e.g., 10.1000/xyz123"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Abstract</label>
                    <textarea
                      name="abstract"
                      value={formData.abstract}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Keywords</label>
                    <input
                      type="text"
                      name="keywords"
                      value={formData.keywords}
                      onChange={handleChange}
                      placeholder="e.g., machine learning, AI, data science"
                    />
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Publications;
